import { ListingType } from '@aloft/toolkit-shared';

import {
  removeComp as removeCompAction,
  removeComps as removeCompsAction,
} from '../../redux/features/actions/removeComp.ts';
import { setCompSequence, setRentalCompSequence } from '../../redux/features/orderSlice.ts';
import {
  selectCompIndexById,
  selectCompSequenceByListingType,
  selectRentalCompIndexById,
} from '../../redux/features/selectors/orderSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';

export const useCompActions = (propertyId: string, listingType?: ListingType) => {
  const dispatch = useAppDispatch();
  const useRental = listingType === 'rentalListing';
  const compIndex = useAppSelector((state) =>
    useRental
      ? selectRentalCompIndexById(state, propertyId)
      : selectCompIndexById(state, propertyId),
  );
  const compSequence = useAppSelector((state) =>
    selectCompSequenceByListingType(state, listingType),
  );

  const handleUpdateCompSequence = (newCompSequence: string[]) => {
    if (useRental) {
      dispatch(setRentalCompSequence(newCompSequence));
    } else {
      dispatch(setCompSequence(newCompSequence));
    }
  };

  const moveComp = (currIndex: number, newIndex: number) => {
    if (currIndex >= compSequence.length || newIndex >= compSequence.length) {
      console.error('Invalid index');
      return;
    }

    const newCompSequence = [...compSequence];
    const [removed] = newCompSequence.splice(currIndex, 1);
    if (removed === undefined) {
      console.error('Invalid index, comp not found');
      return;
    }
    newCompSequence.splice(newIndex, 0, removed);

    handleUpdateCompSequence(newCompSequence);
  };

  const moveCompLeft = () => {
    if (compIndex !== undefined && compIndex > 0) {
      moveComp(compIndex, compIndex - 1);
    }
  };

  const moveCompRight = () => {
    if (compIndex !== undefined && compIndex < compSequence.length - 1) {
      moveComp(compIndex, compIndex + 1);
    }
  };

  const removeComp = (compId: string) => {
    const newCompSequence = compSequence.filter((id) => id !== compId);
    handleUpdateCompSequence(newCompSequence);
    dispatch(removeCompAction(compId));
  };

  const removeComps = (compIds: string[]) => {
    dispatch(
      removeCompsAction({
        ids: compIds,
        sequenceTarget: useRental ? 'rentalCompSequence' : 'compSequence',
      }),
    );
  };

  return {
    moveCompLeft,
    moveCompRight,
    removeComp,
    removeComps,
  };
};
